import RequestForm from "@components/helpCenter/RequestForm"
import { getWebAccessEndDate } from "@helpers/helpCenterResult"
import { RefundCompletedPageLocationState } from "@pages/helpCenter/RefundCompletedPage"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useLoaderData, useNavigate } from "react-router-dom"
import RefundMultipleSubscriptionsDialog from "../../components/helpCenter/RefundMultipleSubscriptionsDialog"
import { refundRevenueCatSubscriptions } from "../../helpers/supabase"
import { GetSubscriptionsLoaderData } from "../../loaders/getSubscriptionsLoader"

export type RefundType = "all" | "web"

export default function RefundPage() {
  const [displayDialog, setDisplayDialog] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState<RefundType | null>(null)

  const { refundableSubscriptions, userEmail } =
    useLoaderData() as GetSubscriptionsLoaderData

  const navigate = useNavigate()
  const { t } = useTranslation()

  const hasMultipleNonRefundedSubscriptions = refundableSubscriptions.length > 1

  const hasNoNonRefundedSubscriptions = refundableSubscriptions.length === 0

  const refundSubscriptions = async (refundType: RefundType) => {
    const {
      activeAppStoreSubscriptionsCount,
      activePlayStoreSubscriptionsCount,
      activeStripeSubscriptionsCount,
      canceledStripeSubscriptionsCount,
      refundedStripeSubscriptionsCount,
    } = await refundRevenueCatSubscriptions()

    const state: RefundCompletedPageLocationState = {
      // NOTE: A web subscription that isn't refunded is canceled at period
      // end: the access ends when the subscription would have renewed.
      accessEndDate: getWebAccessEndDate(refundableSubscriptions),
      activeAppStoreSubscriptionsCount,
      activePlayStoreSubscriptionsCount,
      activeStripeSubscriptionsCount,
      canceledStripeSubscriptionsCount,
      refundedStripeSubscriptionsCount,
      refundType,
    }

    navigate("/help-center/refund/completed", { replace: true, state })
  }

  const handleClick = async () => {
    setIsSubmitting("all")

    if (hasMultipleNonRefundedSubscriptions) {
      setDisplayDialog(true)
    } else {
      await refundSubscriptions("all")
    }

    setIsSubmitting(null)
  }

  return (
    <>
      <RequestForm
        buttonTitle={t("pages.helpCenter.refund.requestRefund")}
        email={userEmail}
        hasNoSubscription={hasNoNonRefundedSubscriptions}
        heading={t("pages.helpCenter.refund.heading")}
        onSubmit={() => void handleClick()}
        subject={t("pages.helpCenter.requestRefund")}
        submitting={!!isSubmitting}
      />

      {displayDialog && (
        <RefundMultipleSubscriptionsDialog
          isSubmitting={isSubmitting}
          refundableSubscriptions={refundableSubscriptions}
          onClose={() => setDisplayDialog(false)}
          open
          refundSubscriptions={refundSubscriptions}
          setIsSubmitting={setIsSubmitting}
        />
      )}
    </>
  )
}
